import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { getErrorMessage, Alert, AlertDescription, Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, Textarea, useToast, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@nimbitlabs/ui';

interface Service {
  id: string;
  name: string;
}

interface Environment {
  id: string;
  name: string;
}

interface ServiceEnvironment {
  id: string;
  serviceId: string;
  environmentId: string;
}

interface CreateMonitoringConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateMonitoringConfigModal: React.FC<CreateMonitoringConfigModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [serviceEnvironments, setServiceEnvironments] = useState<ServiceEnvironment[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  // Error states
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    serviceId: '',
    environmentId: '',
    url: '',
    method: 'GET',
    timeoutSeconds: 30,
    intervalMinutes: 5,
    expectedStatusCode: 200,
    expectedResponseBody: '',
    expectedResponseHeader: '',
    authType: 'None',
    authHeaderName: '',
    authHeaderValue: '',
    retryCount: 3,
    retryDelaySeconds: 5,
    failureThreshold: 3,
    successThreshold: 1,
    enableAlerts: true,
    alertCooldownMinutes: 15,
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      setLoadError(null);
      const [servicesRes, environmentsRes, serviceEnvsRes] = await Promise.all([
        api.services.getAll(),
        api.environments.getAll(),
        api.serviceEnvironments.getAll(),
      ]);
      setServices(servicesRes);
      setEnvironments(environmentsRes);
      setServiceEnvironments(serviceEnvsRes);
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoadError(getErrorMessage(error));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    if (!formData.serviceId || !formData.environmentId) {
      setSubmitError('Please select a service and environment');
      return;
    }

    // Find the service environment ID
    const serviceEnv = serviceEnvironments.find(
      se => se.serviceId === formData.serviceId && se.environmentId === formData.environmentId
    );

    if (!serviceEnv) {
      setSubmitError('Service environment not found. Please ensure the service is deployed to this environment.');
      return;
    }

    try {
      setLoading(true);
      await api.serviceMonitoringConfigurations.create({
        serviceEnvironmentId: serviceEnv.id,
        url: formData.url,
        method: formData.method as any,
        timeoutSeconds: formData.timeoutSeconds,
        intervalMinutes: formData.intervalMinutes,
        expectedStatusCode: formData.expectedStatusCode,
        expectedResponseBody: formData.expectedResponseBody || null,
        expectedResponseHeader: formData.expectedResponseHeader || null,
        authType: formData.authType as any,
        authHeaderName: formData.authHeaderName || null,
        authHeaderValue: formData.authHeaderValue || null,
        retryCount: formData.retryCount,
        retryDelaySeconds: formData.retryDelaySeconds,
        failureThreshold: formData.failureThreshold,
        successThreshold: formData.successThreshold,
        enableAlerts: formData.enableAlerts,
        alertCooldownMinutes: formData.alertCooldownMinutes,
      });

      toast({
        title: 'Success',
        description: 'Monitoring created successfully',
      });
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create monitoring configuration:', error);
      setSubmitError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Monitoring</DialogTitle>
          <DialogDescription>
            Configure automated health checks for a service in a specific environment
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Display */}
          {(loadError || submitError) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{loadError || submitError}</AlertDescription>
            </Alert>
          )}
          
          {/* Service and Environment Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="service">Service *</Label>
              {services.length === 0 ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    You need to create a service first. Please close this dialog and create a service before setting up monitoring.
                  </AlertDescription>
                </Alert>
              ) : (
                <Select
                  value={formData.serviceId}
                  onValueChange={(value) => setFormData({ ...formData, serviceId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="environment">Environment *</Label>
              <Select
                value={formData.environmentId}
                onValueChange={(value) => setFormData({ ...formData, environmentId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select environment" />
                </SelectTrigger>
                <SelectContent>
                  {environments.map((env) => (
                    <SelectItem key={env.id} value={env.id}>
                      {env.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* HTTP Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">HTTP Configuration</h3>
            
            <div className="space-y-2">
              <Label htmlFor="url">Health Check URL *</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://api.example.com/health"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="method">HTTP Method</Label>
                <Select
                  value={formData.method}
                  onValueChange={(value) => setFormData({ ...formData, method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="HEAD">HEAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectedStatusCode">Expected Status Code</Label>
                <Input
                  id="expectedStatusCode"
                  type="number"
                  value={formData.expectedStatusCode}
                  onChange={(e) => setFormData({ ...formData, expectedStatusCode: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expectedResponseBody">Expected Response Body (optional)</Label>
              <Input
                id="expectedResponseBody"
                value={formData.expectedResponseBody}
                onChange={(e) => setFormData({ ...formData, expectedResponseBody: e.target.value })}
                placeholder="e.g., 'OK' or 'healthy'"
              />
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Timing</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intervalMinutes">Check Interval</Label>
                <Select
                  value={formData.intervalMinutes.toString()}
                  onValueChange={(value) => setFormData({ ...formData, intervalMinutes: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">How often to check (minimum 5 minutes)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeoutSeconds">Timeout (seconds)</Label>
                <Input
                  id="timeoutSeconds"
                  type="number"
                  value={formData.timeoutSeconds}
                  onChange={(e) => setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value) })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">Max wait time</p>
              </div>
            </div>
          </div>

          {/* Retry Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Retry & Thresholds</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="retryCount">Retry Count</Label>
                <Input
                  id="retryCount"
                  type="number"
                  value={formData.retryCount}
                  onChange={(e) => setFormData({ ...formData, retryCount: parseInt(e.target.value) })}
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="retryDelaySeconds">Retry Delay (seconds)</Label>
                <Input
                  id="retryDelaySeconds"
                  type="number"
                  value={formData.retryDelaySeconds}
                  onChange={(e) => setFormData({ ...formData, retryDelaySeconds: parseInt(e.target.value) })}
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="failureThreshold">Failure Threshold</Label>
                <Input
                  id="failureThreshold"
                  type="number"
                  value={formData.failureThreshold}
                  onChange={(e) => setFormData({ ...formData, failureThreshold: parseInt(e.target.value) })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">Consecutive failures to mark unhealthy</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="successThreshold">Success Threshold</Label>
                <Input
                  id="successThreshold"
                  type="number"
                  value={formData.successThreshold}
                  onChange={(e) => setFormData({ ...formData, successThreshold: parseInt(e.target.value) })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">Consecutive successes to mark healthy</p>
              </div>
            </div>
          </div>

          {/* Authentication */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Authentication</h3>
            
            <div className="space-y-2">
              <Label htmlFor="authType">Auth Type</Label>
              <Select
                value={formData.authType}
                onValueChange={(value) => setFormData({ ...formData, authType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="Bearer">Bearer Token</SelectItem>
                  <SelectItem value="ApiKey">API Key</SelectItem>
                  <SelectItem value="Basic">Basic Auth</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.authType !== 'None' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="authHeaderName">Header Name</Label>
                  <Input
                    id="authHeaderName"
                    value={formData.authHeaderName}
                    onChange={(e) => setFormData({ ...formData, authHeaderName: e.target.value })}
                    placeholder="e.g., Authorization, X-API-Key"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="authHeaderValue">Header Value</Label>
                  <Input
                    id="authHeaderValue"
                    type="password"
                    value={formData.authHeaderValue}
                    onChange={(e) => setFormData({ ...formData, authHeaderValue: e.target.value })}
                    placeholder="Token or key value"
                  />
                </div>
              </>
            )}
          </div>

          {/* Alert Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Alerts</h3>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enableAlerts">Enable Alerts</Label>
                <p className="text-sm text-muted-foreground">Trigger alerts when health checks fail</p>
              </div>
              <Switch
                id="enableAlerts"
                checked={formData.enableAlerts}
                onCheckedChange={(checked) => setFormData({ ...formData, enableAlerts: checked })}
              />
            </div>

            {formData.enableAlerts && (
              <div className="space-y-2">
                <Label htmlFor="alertCooldownMinutes">Alert Cooldown (minutes)</Label>
                <Input
                  id="alertCooldownMinutes"
                  type="number"
                  value={formData.alertCooldownMinutes}
                  onChange={(e) => setFormData({ ...formData, alertCooldownMinutes: parseInt(e.target.value) })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">Minimum time between alerts</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Monitoring'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
